import {invalidateLandingContexts, landingContextForPurchase, type LandingObservation} from "analytics/landingVisit";
import {landingObservationGate} from "analytics/landingRequestConsent";
import {rememberLandingErasure, pendingLandingErasures, confirmLandingErasure} from "analytics/landingErasure";
import {getConsent} from "consent/consentStore";
import {clearPlanPickerLandingAttribution} from "pricing/planPickerPress";
import {clearCheckoutLandingAttribution} from "./checkoutHandoff";
import axios from "axios";
import {create} from "@bufbuild/protobuf";
import {createClient, createContextValues} from "@connectrpc/connect";
// The api route that records one authenticated upsell funnel event. It lives in the designated browser-endpoint
// source because the beacon, its e2e assertions and every mocked route fixture must address the same path; a
// second copy is what `deployed-topology-sweep` exists to refuse.
import {OFFER_EVENTS_PATH, currentLadderSendPath} from "../api/endpointPaths.mjs";
import {beaconTransport, fireBeacon} from "../api/beaconTransport";
import {apiTransport, kBeforeSend} from "../api/connectTransport";
import {
	FunnelEventService, RecordLandingObservationRequest_Kind, RecordLandingObservationRequestSchema,
	ReportPaywallEventRequestSchema, WithdrawLandingVisitRequestSchema,
} from "../gen/kitecorp/transferify/common/v1/funnel_events_pb";
import type {OfferEvent, OfferEventEmitter, OfferEventKind, OfferMoment, Unsubscribe} from "./offerEvents";

/**
 * The telemetry SUBSCRIBER for the upsell offer funnel (kitecorp/transferify-api-service#999).
 *
 * Separate from {@link ./offerEvents} on purpose (owner 2026-08-27): the surfaces emit domain events and
 * know nothing about where they go, so measuring the funnel is never a reason to reach into an
 * eligibility rule. This module is the whole of "where they go".
 *
 * ── Why the client reports at all ─────────────────────────────────────────────────────────────────
 * The api cannot see a rendered offer or a pressed button. The offer is decided in the browser from an
 * answer the api gave, and whether it becomes visible depends on rules that live here — so
 * server-witnessed evidence for these moments does not exist to be collected. `UpsellOfferEventKind`
 * says what a row is worth: client-reported, never witnessed.
 *
 * ── TWO TRANSPORTS, ONE SUBSCRIBER, AND THE SPLIT IS THE READER'S SESSION ─────────────────────────
 * Most offer moments are met behind an account, and their report goes on the app's ordinary axios
 * instance, whose request interceptor attaches the Firebase bearer: the api takes the account and its
 * tier from that session rather than from the body, and the account is what lets the funnel reach the
 * purchase that follows — conversion is a join to the buyer's plan, which is not expressible without
 * it.
 *
 * The surfaces in `ANONYMOUS_STEPS_BY_MOMENT` below are not met that way (stated there rather than counted
 * here, because a count in prose rots at every widening). The plan picker is the first thing an ad visitor
 * sees on `/pricing`, the exit offer catches a reader walking away from it, the referral picker greets a
 * friend who has just followed a share link, since ui#2555 the send screen's upsell card, the compact
 * dialog it opens and that dialog's own intro offer are all met by a guest composing an anonymous free
 * send, the promo dialog opens for whoever followed its link, and since ui#3196 the over-limit wall a sender
 * with no session meets is a surface of its own — so their showings were counted for signed-in readers only
 * while the checkout and paid steps behind the sign-in were complete, a floor that every rate divided by it
 * read past (ui#1983, ui#2034).
 * Their APPEARANCE steps and the PRESS on them, plus the referral picker's walk-away, therefore
 * go to the `FunnelEventService.ReportPaywallEvent` call through `api/beaconTransport`, the app's anonymous
 * Connect transport, which is anonymous BY CONTRACT — `credentials: "omit"`, no Authorization header. That
 * contract is not weakened here: the anonymous body is two vocabulary words and carries no account, no tier, no
 * campaign key, no price, no wall and no timestamp, so it stays as uncorrelatable as the consent beacon that
 * transport serves and the download beacon beside it.
 *
 * Coupon shown/click events use authenticated presentation routes, which already own those rows.
 * Other coupon steps use the generic funnel endpoint. Keeping that routing here prevents duplicate
 * rows and keeps API transport details out of the surfaces.
 *
 * ── Fire-and-forget, and never retried ────────────────────────────────────────────────────────────
 * Both properties are contractual, and inherited in spirit from the anonymous transport rather than
 * from its code. The endpoint has no request identity, so a retry writes a second row for one
 * rendering and inflates the very ratio being measured; and a reader must never wait on — or be shown
 * — a measurement failure. A dropped report is the correct failure.
 *
 * The report type binds to the generated API contract. Missing or changed experiment fields and
 * moments therefore fail typecheck instead of silently disappearing from a report.
 *
 * ── A funnel is a floor, never a census ───────────────────────────────────────────────────────────
 * Reports are dropped silently by design: any network failure is swallowed unretried, and an api that
 * predates #999 answers 404 to every one of them. So a moment with no rows was not necessarily unseen,
 * and no consumer may read a gap as an absence.
 */

/** One report's complete body. The account and its plan tier come from the session, never from here. */
export type OfferEventReport = import("api/schemaAssert").Schemas["UpsellOfferEventRequest"];

/**
 * One event as the wire carries it. A pure function, so the shape is testable without a transport.
 *
 * The occasion key is DROPPED rather than sent as null when absent. Two moments have no campaign at
 * all (`PURGED_TRANSFER_HINT` is a retention hint, not a discount) and an always-on coupon has no
 * name, so absence is ordinary; omitting the key keeps the smallest body the api's 512-byte ceiling
 * has to admit and matches how `downloadEventReports` omits an absent password. The two price fields
 * are omitted the same way, for the same reason.
 */
export function offerEventReport(event: OfferEvent): OfferEventReport {
	// SUCCESS_CARD uses percentOff OR amountMinor for the discount, not the resulting invoice price.
	if (event.coupon !== undefined) return {moment: event.moment, kind: event.kind, ...event.coupon};
	return {
		moment: event.moment,
		kind: event.kind,
		...(event.occasion === null || event.occasion === "" ? {} : {occasion: event.occasion}),
		// The card's wall, on the one moment that carries one (ui#2555). Spread as the surface named it:
		// `./upsellCardWall` is where the pairing is decided — the option key present exactly when the
		// trigger is `OPTION` — and restating that rule here would be a second copy of the constraint the
		// api answers 400 from.
		...(event.wall === undefined ? {} : event.wall),
		// The tier a one-tier surface sells and the compact dialog's door (api#1794, #1799): dropped when
		// absent, like the occasion, because every other surface refuses them with a 400.
		...(event.targetPlan === undefined ? {} : {targetPlan: event.targetPlan}),
		...(event.entry === undefined ? {} : {entry: event.entry}),
		// Whether the sender met the over-limit wall signed out first (ui#3196): dropped when absent, for the
		// reason the two above are, and sent as the boolean it is otherwise, because `false` is an answer the api
		// stores apart from "the browser did not say".
		...(event.arrivedSignedOut === undefined ? {} : {arrivedSignedOut: event.arrivedSignedOut}),
		// Lowercase on the wire, because that is the ONE form the api admits and stores — every currency
		// column in its schema holds Stripe's lowercase code, and its Superset joins compare them as is.
		// Every surface already holds lowercase; this makes the contract hold whatever a future one holds.
		// A figure of zero or less is NO figure: the api refuses an amount under one minor unit, and
		// refusing means the whole step is lost, where a step with no price is still a step.
		...(event.price === null || event.price.amountMinor <= 0
			? {}
			: {currency: event.price.currency.toLowerCase(), amountMinor: event.price.amountMinor}),
	};
}

/**
 * Sends one event, and returns whether anything left the browser.
 *
 * Returns SYNCHRONOUSLY, before the request settles: the caller is inside a render effect or a click
 * handler that is about to open a dialog, and neither may wait on a measurement.
 *
 * Exported for the same reason the report builder is: "did this report fire" is the thing worth
 * asserting, and it is not observable from the emitter alone.
 */
export function reportOfferEvent(event: OfferEvent): boolean {
	// The app's single configured api base (`AppProviders` sets it from REACT_APP_URL). Read at call
	// time rather than at module scope: this module is reachable from pre-rendered pages, where no
	// browser environment exists yet and no base has been set.
	if (typeof axios.defaults.baseURL !== "string" || axios.defaults.baseURL === "") {
		return false;
	}
	// The presentation routes own shown/click rows. The generic endpoint receives only later steps.
	const step = event.kind === "OFFER_SHOWN" ? "shown" : event.kind === "OFFER_CLICKED" ? "click" : null;
	const presentationPath = event.coupon !== undefined && step !== null
		? `${currentLadderSendPath(event.coupon.presentationId)}/${step}` : null;
	void (presentationPath === null ? axios.post(OFFER_EVENTS_PATH, offerEventReport(event)) : axios.post(presentationPath)).catch(() => {
		// A report that did not arrive is a missing datapoint, never a user-facing error and never a
		// reason to retry. See this module's header: a retry would double-count.
	});
	return true;
}

/** The two steps an APPEARANCE produces on its own. */
const APPEARANCE_STEPS: readonly OfferEventKind[] = ["OFFER_SHOWN", "PRICE_SEEN"];

/**
 * The appearance plus the PRESS, admitted on every anonymously reachable surface (ui#2663).
 *
 * The press is the last step an anonymous reader takes, because the press is what RAISES the sign-in:
 * `resolveUpgradeAction` answers `{kind: "login"}` for every one of them. Until it was admitted, a
 * press that opened the login modal reached no counter at all, so `checkout_opened / price_seen` on
 * these surfaces could not tell "nobody pressed" from "pressed and stopped at the wall" — the 288-to-4
 * collapse this widening exists to split.
 */
const PRESSED_STEPS: readonly OfferEventKind[] = [...APPEARANCE_STEPS, "OFFER_CLICKED"];

/**
 * The surfaces an anonymous reader can meet, and the steps they produce THERE (ui#1983, widened by
 * ui#2034).
 *
 * A table rather than a derivation, and it MIRRORS the api's own answer — `UpsellMoment
 * .isAnonymouslyReportable()` and `UpsellOfferEventKind.isAnonymouslyReportable(UpsellMoment)`, which is
 * where the rule is enforced. The two cannot be one constant across two repositories, so the api is the
 * authority and a drift is visible rather than silent: a report this side sends and that side does not
 * admit is counted on `paywall.events.rejected` as `unreportable_moment` or `unreportable_step`, which
 * are the two reasons the api documents as worth an alarm.
 *
 * It is a table PER SURFACE rather than two lists because the admitted set stopped being a product of
 * them at ui#2034: the referral picker opens on the landing a share link points at, so its walk-away is
 * pressed before the friend has an account, and it is the only surface here whose ✕ an anonymous reader
 * ever reaches. On every surface the CHECKOUT and the PAYMENT sit behind a sign-in, so the
 * authenticated route already counts them completely and sending one anonymously would report a step
 * nobody took.
 */
const ANONYMOUS_STEPS_BY_MOMENT: Partial<Record<OfferMoment, readonly OfferEventKind[]>> = {
	PLAN_UPGRADE: PRESSED_STEPS,
	EXIT_OFFER: PRESSED_STEPS,
	// The three surfaces of ui#2555, admitted anonymously by api#1777/#1772 for the reason the two above
	// are: a guest composing a transfer reaches for the same locked controls a signed-in sender does, and
	// the send screen is where an anonymous free send starts — so counting only the signed-in half would
	// make every rate on these three a rate over the remainder. Appearance and press only, exactly as
	// above: the checkout and the payment both sit behind a sign-in, where the authenticated route
	// already counts them completely.
	UPSELL_CARD: PRESSED_STEPS,
	COMPACT_DIALOG: PRESSED_STEPS,
	COMPACT_EXIT_OFFER: PRESSED_STEPS,
	// `INVITE_OPENED` joined the walk-away here at kitecorp/transferify-api-service#1582, and for the same
	// reason: a referral link is shared with somebody who does not have an account — that is the whole
	// point of it — so the open happens before any session exists. Counting only signed-in openers would
	// make `invite_opened / invite_sent` a rate over the remainder rather than over the traffic. Its
	// sibling `INVITE_SENT` is deliberately absent: a referrer has a session by definition, so an
	// anonymous report of it could only be a caller inflating the denominator every rate below divides by.
	REFERRAL_TRIAL: [...PRESSED_STEPS, "OFFER_DISMISSED", "INVITE_OPENED"],
	// The `?promo=` dialog opens, with its price, for a signed-out visitor and raises the sign-in only at the
	// press (owner decision 2026-09-27, kitecorp/transferify-ui#2914, api#2215). Appearance, press and the ✕,
	// which a signed-out reader reaches as the referral picker's reader does. Until the api admits this moment
	// anonymously these reports are refused and counted as `unreportable_moment`, which costs the reader nothing.
	PROMO_LINK: [...PRESSED_STEPS, "OFFER_DISMISSED"],
	// The over-limit wall as a sender with NO session meets it (kitecorp/transferify-ui#3196), one surface per
	// pricing rule like the priced walls. Met signed out by construction, since a signed-in sender is shown the
	// priced wall instead, so its showing is anonymous and its press IS the sign-in: the last anonymous act, as
	// on every surface above. The price step is admitted with the other two for the price-before-sign-in work
	// that follows; nothing sends it yet. What the sender does after the sign-in is reported on the priced walls,
	// signed in, with `arrivedSignedOut` set.
	OVER_LIMIT_HOSTED_SIGNED_OUT: PRESSED_STEPS,
	OVER_LIMIT_INSTANT_SIGNED_OUT: PRESSED_STEPS,
};

/** Whether this event is one the anonymous endpoint admits. A pure rule, so it is testable alone. */
export function isAnonymouslyReportable(event: OfferEvent): boolean {
	return (ANONYMOUS_STEPS_BY_MOMENT[event.moment] ?? []).includes(event.kind);
}

/**
 * One anonymous report as the wire carries it: the surface and the step, and nothing else.
 *
 * The campaign key, the price and the card's WALL are DROPPED rather than merely unused, and that is the
 * privacy argument rather than a saving. `paywall.events` carries a surface and a step and nothing else,
 * so none of them would reach a series; the authenticated route records them because a ROW can hold them,
 * and this route writes no row about the report (since kitecorp/transferify-api-service#2535 it adds one to a
 * per-day count keyed on the storefront, the surface and the step, which has no place for them either). A
 * per-wall dimension on the meter would also multiply the billed series
 * by the wall vocabulary, which is the api's own reason for keeping the trigger authenticated-only
 * (api#1777). The api's own body has nowhere to put a third field either.
 */
export function anonymousOfferEventReport(event: OfferEvent): {moment: OfferMoment; kind: OfferEventKind} {
	return {moment: event.moment, kind: event.kind};
}

/**
 * Sends one anonymous report, and returns whether anything left the browser.
 *
 * Through `fireBeacon` rather than axios: no credential may ride on this, and the fire-and-forget,
 * never-retried, never-awaited contract is that transport's own. A retry would count a second
 * appearance for one that happened once, which is the very denominator this exists to repair.
 */
export function reportAnonymousOfferEvent(event: OfferEvent): boolean {
	return fireBeacon(transport => createClient(FunnelEventService, transport).reportPaywallEvent(
		create(ReportPaywallEventRequestSchema, anonymousOfferEventReport(event))));
}

/**
 * Subscribes the beacon to an emitter and returns the unsubscribe.
 *
 * Wired from `App`, the shell — not from any one surface. The moments span the success card, the
 * files panel's callouts, the download-usage banner and a purged row on the transfers page, so every
 * narrower host unmounts partway through the funnel. It is the same reasoning that puts referral and
 * advertising attribution on the shell rather than on the landing page.
 *
 * <b>`hasAccount` is the transport's own check, not a surface's</b> (kitecorp/transferify-ui#1965, routed
 * rather than dropped since ui#1983). It chooses the transport: with a session the report goes to the
 * authenticated endpoint, which resolves the account and its tier and writes the row Superset joins;
 * without one it goes to the anonymous endpoint if the event is one that endpoint admits, and is dropped
 * otherwise. Dropping is still right for the rest: a report the api refuses would cost a console error and
 * count nothing, and every step past the appearance is already complete on the authenticated side.
 *
 * The consequence is stated rather than hidden. On PLAN_UPGRADE, EXIT_OFFER, REFERRAL_TRIAL, UPSELL_CARD,
 * COMPACT_DIALOG, COMPACT_EXIT_OFFER, PROMO_LINK, OVER_LIMIT_HOSTED_SIGNED_OUT and OVER_LIMIT_INSTANT_SIGNED_OUT
 * at OFFER_SHOWN, PRICE_SEEN and OFFER_CLICKED, and on
 * REFERRAL_TRIAL at OFFER_DISMISSED and INVITE_OPENED, the funnel now counts every reader, so those
 * pairs are complete; every other surface and step is still signed-in
 * only, and correctly so, because they are met behind an account. INVITE_SENT is signed-in only by
 * construction rather than by omission: only an account has an invite to hand out. The
 * ROWS stay signed-in only throughout — an anonymous reader has no account for one to belong to — so the
 * metric and the table answer different questions on those pairs, which is what
 * `analytics/dashboards/upsell_offer_conversion.sql` says at the top.
 *
 * <b>Stated WITHOUT a count on purpose</b> (kitecorp/transferify-ui#2663). This paragraph said "fourteen
 * pairs" in one sentence and "eight pairs" in the next, about the same set, and both were already wrong
 * before the press joined it. `UpsellMoment.isAnonymouslyReportable`'s javadoc makes the same choice for
 * the same reason: an ordinal in a comment is the first thing that rots when the table grows, and the
 * table above is the answer.
 */
export function wireOfferTelemetry(
	emitter: OfferEventEmitter, hasAccount: () => boolean = () => true,
): Unsubscribe {
	return emitter.subscribe(event => {
		if (hasAccount()) {
			reportOfferEvent(event);
			return;
		}
		if (isAnonymouslyReportable(event)) {
			reportAnonymousOfferEvent(event);
		}
	});
}

const erasuresInFlight = new Set<string>();

/**
 * Sends one landing observation on the api's transport, so a signed-in visitor's report carries the bearer. From
 * kitecorp/transferify-api-service#3146 on, the api's `RecordLandingObservation` adapter takes the optional caller
 * from it, and an anonymous visitor's report is still served. Returns whether it was started (not whether it arrived).
 *
 * The consent check is the last step before the request leaves ({@link landingObservationGate}): consent can be
 * withdrawn while the token is being read, and an observation is then cancelled, with the rejection dropped.
 * Never awaited and never retried by the caller; a missing observation never interrupts the draft or a purchase.
 */
export function reportLandingObservation(observation: LandingObservation): boolean {
	const attribution = landingContextForPurchase(observation.attribution, getConsent().analytics);
	if (!attribution) return false;
	if (!rememberLandingErasure(attribution)) return false;
	void createClient(FunnelEventService, apiTransport).recordLandingObservation(
		create(RecordLandingObservationRequestSchema,
			{kind: RecordLandingObservationRequest_Kind[observation.kind], attribution}),
		{contextValues: createContextValues().set(kBeforeSend, landingObservationGate(attribution))},
	).catch(() => {
		// A missing observation never interrupts the draft or a purchase, and a cancelled one is the consent check at work.
	});
	return true;
}

/** A withdrawal is an erasure request, not another analytics event; send it after consent becomes false. */
export async function syncLandingAnalyticsConsent(consent: {analytics: boolean}): Promise<void> {
	if (!consent.analytics) {
		invalidateLandingContexts();
		clearPlanPickerLandingAttribution();
		// Recover older persisted handoffs before removing their attribution.
		clearCheckoutLandingAttribution();
	}
	const visits = pendingLandingErasures(!consent.analytics);
	await Promise.all(visits.map(async visitId => {
		if (erasuresInFlight.has(visitId)) return;
		erasuresInFlight.add(visitId);
		try {
			// The visit id is the capability to erase the visit, so the call is anonymous and carries no bearer; awaited,
			// because the capability may be forgotten only once the api has answered.
			await createClient(FunnelEventService, beaconTransport)
				.withdrawLandingVisit(create(WithdrawLandingVisitRequestSchema, {visitId}));
			confirmLandingErasure(visitId);
		} catch {
			// The erasure is idempotent and retries on online, consent changes and the next document's startup.
			// The retained capability can erase only; analytics context remains cleared.
		} finally {
			erasuresInFlight.delete(visitId);
		}
	}));
}
